import { Injectable } from "@angular/core";
import { LanguageService } from '../../../../../shared/services/language.service';
import { AmsNotifierService } from '../../../../../shared/services/ams-notifier.service';
import * as moment from 'moment';
import { TimeConverter } from "src/app/shared/services/time-converter.service";

export interface ConvertResult {
  isValid: boolean;
  timeSalariesToAdd?: any [];
  timeSalariesToUpdate?: any[];
  timeSalariesToUpdateWithOriginalPKValues?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class GridToTimeSalaryConverter {
  showEmployeeColumn: boolean;

  constructor(private languageService: LanguageService, private amsNotifierService: AmsNotifierService, private timeConverter: TimeConverter) {}

  convert(gridRows: any[], employeeId: number, newStatusId: number, newCategoryId: number, showEmployeeColumn: boolean = true): ConvertResult {
    this.showEmployeeColumn = showEmployeeColumn;

    const result: ConvertResult = { isValid: true, timeSalariesToAdd: [], timeSalariesToUpdate: [], timeSalariesToUpdateWithOriginalPKValues: [] };

    gridRows.forEach((row: any) => {
      if (row.type === "insert") {
        result.timeSalariesToAdd.push(this.convertRow(row, row.data.EmployeeId ?? employeeId, newStatusId, newCategoryId, newStatusId));
      } else if (row.type === "update") {
        result.timeSalariesToUpdate.push(this.convertRow(row, row.data.EmployeeId ?? row.key.EmployeeId ?? employeeId, row.key.StatusId, row.key.CategoryId, newStatusId));
        result.timeSalariesToUpdateWithOriginalPKValues.push({
          EmployeeId: row.key.EmployeeId,
          StatusId: row.key.StatusId,
          CategoryId: row.key.CategoryId,
          CarrierId: row.key.CarrierId,
          ProjectId: row.key.ProjectId,
          Date: moment(row.key.Date).format()
        });
      }
    });

    const isValid = this.validateRows(result);

    return isValid ? result : { isValid: false };
  }

  private convertRow(row: any, employeeId: number, statusId: number, categoryId: number, statusIdToReplaceCopyStatus: number): any {
    const hasChangedFromProject = row.data.FromProjectNumber !== undefined && row.data.FromProjectNumber !== row.key.FromProjectNumber;
    const hasChangedToProject = row.data.ToProjectNumber !== undefined && row.data.ToProjectNumber !== row.key.ToProjectNumber;

    return {
      EmployeeId: employeeId,
      Date: row.data.Date ? moment(row.data.Date).format() : moment(row.key.Date).format(),
      CarrierId: row.data.CarrierId ? row.data.CarrierId : row.key.CarrierId,
      CarrierProjectId: row.data.CarrierProjectId ? row.data.CarrierProjectId : row.key.CarrierProjectId,
      Project: row.data.Project ? row.data.Project : row.key.Project,
      ProjectId: row.data.ProjectId !== undefined ? row.data.ProjectId : row.key.ProjectId,
      FromProjectNumber: row.data.FromProjectNumber,
      HasChangedFromProject: hasChangedFromProject,
      ToProjectNumber: row.data.ToProjectNumber,
      HasChangedToProject: hasChangedToProject,
      Comment: row.data.Comment !== undefined ? row.data.Comment : row.key.Comment,
      ProjectActivityUse: row.data.ProjectActivityUse !== undefined ? row.data.ProjectActivityUse : row.key.ProjectActivityUse,
      StatusId: statusId,
      CategoryId: categoryId,
      StatusIdToReplaceCopyStatus: statusIdToReplaceCopyStatus,
      TimesAggregatedByTimeType: this.getTimeTypeColumns(row)
    };
  }

  private getTimeTypeColumns(row: any): any[] {
    const timeTypeColumns = [];
    const columns = row.type === "insert"
      ? Object.keys(row.data).filter(key => key.startsWith('TimeType_'))
      : Object.keys(row.key).filter(key => key.startsWith('TimeType_'));

    for (const column of columns) {
      const ttCol = this.getTimeTypeColumn(row, column);
      if (row.type !== "insert" || (row.type === "insert" && ttCol.Seconds > 0)) {
        timeTypeColumns.push(ttCol);
      }
    }

    return timeTypeColumns;
  }

  private getTimeTypeColumn(row: any, column: any): any {
    const value = row.data[column] ?? row.key[column];

    return {
      TimeTypeId: parseInt(column.split('_')[1], 10),
      Seconds: this.timeConverter.getTimeInputConvertedToSeconds(value)
    };
  }

  private validateRows(convertedResult: ConvertResult): boolean {
    for (const row of convertedResult.timeSalariesToAdd) {
      if (!this.isValid(row)) { return false; }
    }

    for (const row of convertedResult.timeSalariesToUpdate) {
      if (!this.isValid(row)) { return false; }
    }

    return true;
  }

  isValid(row: any): boolean {
    if (!row.Date || !moment(row.Date).isValid()) {
      this.amsNotifierService.errorWithTimeout(this.languageService.LNG("Please.Select.Date"));
      return false;
    }

    if (!row.CarrierProjectId || row.CarrierProjectId <= 0) {
      this.amsNotifierService.errorWithTimeout(this.languageService.LNG("Please.Select.Project"));
      return false;
    }

    if (this.showEmployeeColumn && (!row.EmployeeId || row.EmployeeId <= 0)) {
      this.amsNotifierService.errorWithTimeout(this.languageService.LNG("Please.Select.Employee"));
      return false;
    }

    if ((!row.ProjectActivityUse || row.ProjectActivityUse === 0) && row.ProjectId && row.ProjectId > 0) {
      this.amsNotifierService.errorWithTimeout(this.languageService.LNG("Error.Projectactivity.Is.Not.Allowed.On.The.Selected.Project"));
      return false;
    }

    if ((row.ProjectActivityUse && row.ProjectActivityUse === 2) && (!row.ProjectId || row.ProjectId <= 0)) {
      this.amsNotifierService.errorWithTimeout(this.languageService.LNG("Error.Projectactivity.Is.Required.On.The.Selected.Project"));
      return false;
    }

    if (!this.hasAssignedAnyTimeType(row)) {
      this.amsNotifierService.errorWithTimeout(this.languageService.LNG("Label.Please.Type.Hours"));
      return false;
    }

    return true;
  }

  private hasAssignedAnyTimeType(row: any): boolean {
    return row.TimesAggregatedByTimeType?.filter((timeType: any) => timeType.Seconds > 0).length > 0;
  }
}
